import axios from 'axios';

// Configurar la URL base del API
// En desarrollo usar localhost, en producción usar la URL del servidor
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://reparacion.sistemasintersecom.com/backend/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// ==================== ÓRDENES ====================

export const ordenesApi = {
  // Listar todas las órdenes
  getAll: async (params = {}) => {
    const response = await api.get('/ordenes.php', { params });
    return response.data;
  },

  // Obtener una orden por ID
  getById: async (id) => {
    const response = await api.get(`/ordenes.php?id=${id}`);
    return response.data;
  },

  // Crear nueva orden
  create: async (data) => {
    const response = await api.post('/ordenes.php', data);
    return response.data;
  },

  // Actualizar orden
  update: async (id, data) => {
    const response = await api.put(`/ordenes.php?id=${id}`, data);
    return response.data;
  },

  // Eliminar orden
  delete: async (id) => {
    const response = await api.delete(`/ordenes.php?id=${id}`);
    return response.data;
  },

  // Buscar órdenes
  search: async (query, extraParams = {}) => {
    const response = await api.get('/ordenes.php', { params: { action: 'buscar', q: query, ...extraParams } });
    return response.data;
  },

  // Obtener estadísticas
  getStats: async () => {
    const response = await api.get('/ordenes.php', { params: { action: 'estadisticas' } });
    return response.data;
  },

  // Buscar cliente por teléfono
  buscarCliente: async (telefono) => {
    const response = await api.get('/ordenes.php', { params: { action: 'buscar_cliente', telefono } });
    return response.data;
  },

  // Entregar orden y registrar pago
  entregar: async (data) => {
    const response = await api.post('/ordenes.php?action=entregar', data);
    return response.data;
  },
};

// ==================== CLIENTES ====================

export const clientesApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/clientes.php', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/clientes.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/clientes.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/clientes.php?id=${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/clientes.php?id=${id}`);
    return response.data;
  },

  search: async (query) => {
    const response = await api.get('/clientes.php', { params: { q: query } });
    return response.data;
  },
};

// ==================== EMPLEADOS ====================

export const empleadosApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/empleados.php', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/empleados.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/empleados.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/empleados.php?id=${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/empleados.php?id=${id}`);
    return response.data;
  },
};

// ==================== TIENDAS ====================

export const tiendasApi = {
  getAll: async () => {
    const response = await api.get('/tiendas.php');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/tiendas.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/tiendas.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/tiendas.php?id=${id}`, data);
    return response.data;
  },
};

// ==================== AUTENTICACIÓN ====================

export const authApi = {
  // Login
  login: async (usuario, password) => {
    const response = await api.post('/auth.php', {
      action: 'login',
      usuario,
      password
    });
    return response.data;
  },

  // Logout
  logout: async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      await api.post('/auth.php', { action: 'logout' }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },

  // Verificar sesión
  verificarSesion: async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    const response = await api.get('/auth.php', {
      params: { action: 'verificar' },
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  },

  // Cambiar contraseña
  cambiarPassword: async (password_actual, password_nuevo) => {
    const token = localStorage.getItem('auth_token');
    const response = await api.post('/auth.php', {
      action: 'cambiar_password',
      password_actual,
      password_nuevo
    }, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.data;
  }
};

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==================== REPORTES ====================

export const reportesApi = {
  // Resumen general
  getResumen: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'resumen', ...params } });
    return response.data;
  },

  // Ingresos por día
  getIngresos: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'ingresos', ...params } });
    return response.data;
  },

  // Por tipo de servicio
  getPorServicios: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'servicios', ...params } });
    return response.data;
  },

  // Por técnico
  getPorTecnicos: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'tecnicos', ...params } });
    return response.data;
  },

  // Por estados
  getPorEstados: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'estados', ...params } });
    return response.data;
  },

  // Ingresos mensuales
  getIngresosMensuales: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'mensual', ...params } });
    return response.data;
  },

  // Clientes frecuentes
  getClientesFrecuentes: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'clientes_frecuentes', ...params } });
    return response.data;
  },

  // Pendientes de cobro
  getPendientesCobro: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'pendientes_cobro', ...params } });
    return response.data;
  },

  // Comparativo de sucursales
  getComparativoSucursales: async (params = {}) => {
    const response = await api.get('/reportes.php', { params: { tipo: 'comparativo_sucursales', ...params } });
    return response.data;
  },
};

// ==================== UPLOAD DE IMÁGENES ====================

export const uploadApi = {
  // Subir imagen
  subirImagen: async (file) => {
    const formData = new FormData();
    formData.append('imagen', file);

    const response = await axios.post(`${API_BASE_URL}/upload.php`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Eliminar imagen
  eliminarImagen: async (nombre) => {
    const response = await api.delete('/upload.php', { data: { nombre } });
    return response.data;
  },
};

// ==================== COTIZACIONES ====================

export const cotizacionesApi = {
  // Listar todas las cotizaciones
  getAll: async (params = {}) => {
    const response = await api.get('/cotizaciones.php', { params });
    return response.data;
  },

  // Obtener una cotización por ID
  getById: async (id) => {
    const response = await api.get(`/cotizaciones.php?id=${id}`);
    return response.data;
  },

  // Crear nueva cotización
  create: async (data) => {
    const response = await api.post('/cotizaciones.php', data);
    return response.data;
  },

  // Actualizar cotización
  update: async (id, data) => {
    const response = await api.put(`/cotizaciones.php?id=${id}`, data);
    return response.data;
  },

  // Eliminar cotización
  delete: async (id) => {
    const response = await api.delete(`/cotizaciones.php?id=${id}`);
    return response.data;
  },

  // Buscar cotizaciones
  search: async (query) => {
    const response = await api.get('/cotizaciones.php', { params: { action: 'buscar', q: query } });
    return response.data;
  },

  // Obtener estadísticas
  getStats: async (params = {}) => {
    const response = await api.get('/cotizaciones.php', { params: { action: 'estadisticas', ...params } });
    return response.data;
  },

  // Cotizaciones por usuario
  getPorUsuario: async (params = {}) => {
    const response = await api.get('/cotizaciones.php', { params: { action: 'por_usuario', ...params } });
    return response.data;
  },

  // Registrar envío por WhatsApp
  registrarEnvioWhatsapp: async (data) => {
    const response = await api.post('/cotizaciones.php?action=enviar_whatsapp', data);
    return response.data;
  },

  // Agregar seguimiento
  agregarSeguimiento: async (data) => {
    const response = await api.post('/cotizaciones.php?action=agregar_seguimiento', data);
    return response.data;
  },

  // Cambiar estado
  cambiarEstado: async (data) => {
    const response = await api.post('/cotizaciones.php?action=cambiar_estado', data);
    return response.data;
  },

  // Duplicar cotización
  duplicar: async (data) => {
    const response = await api.post('/cotizaciones.php?action=duplicar', data);
    return response.data;
  },

  // Obtener seguimientos
  getSeguimientos: async (id) => {
    const response = await api.get('/cotizaciones.php', { params: { action: 'seguimientos', id } });
    return response.data;
  },
};

// ==================== RECIBOS DE CAJA ====================

export const recibosApi = {
  // Listar recibos
  getAll: async (params = {}) => {
    const response = await api.get('/recibos.php', { params });
    return response.data;
  },

  // Obtener recibo por ID
  getById: async (id) => {
    const response = await api.get(`/recibos.php?id=${id}`);
    return response.data;
  },

  // Crear nuevo recibo
  create: async (data) => {
    const response = await api.post('/recibos.php', data);
    return response.data;
  },

  // Eliminar recibo
  delete: async (id) => {
    const response = await api.delete(`/recibos.php?id=${id}`);
    return response.data;
  },

  // Buscar recibos
  search: async (query) => {
    const response = await api.get('/recibos.php', { params: { action: 'buscar', q: query } });
    return response.data;
  },

  // Estadísticas
  getStats: async (params = {}) => {
    const response = await api.get('/recibos.php', { params: { action: 'estadisticas', ...params } });
    return response.data;
  },
};

export default api;
